import {
  RouteProp,
  useFocusEffect,
  useNavigation,
  useRoute
} from '@react-navigation/core';
import { StackActions } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import {
  default as React,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import styled from 'styled-components/native';
import ActivityIndicatorScreen from '../../components/ActivityIndicatorScreen';
import AlertBox from '../../components/AlertBox';
import Button from '../../components/Button';
import DynamicForm, {
  DynamicFormBackApi
} from '../../dynamic-forms/DynamicForm';
import {
  useFormNavigation,
  useRegisterFormBack
} from '../../dynamic-forms/navigation/FormNavigationContext';
import {
  appendSectionHistory,
  parseSectionHistory
} from '../../dynamic-forms/navigation/formBack';
import DynamicFormProvider from '../../dynamic-forms/DynamicFormProvider';
import { mergeDraftValues } from '../../dynamic-forms/drafts/formDraftStore';
import { useFormDraft } from '../../dynamic-forms/drafts/useFormDraft';
import { useUserContext } from '../../context/UserContext';
import { useGetStudyParticipantEventForm } from '../../hooks/study-participant/useGetStudyParticipantEventForm';
import { StudiesParamList } from '../../navigation/StudiesNavigator';
import { getEventOccurrenceDate } from './helpers/ParticipantEventHelpers';
import StudyEventFormDataSourceProvider, {
  StudyEventFormDataSourceContext
} from './StudyEventFormDataSource';

const StyledUnavailableView = styled.View`
  flex: 1;
  background-color: #fff;
  padding: 24px;
  justify-content: center;
`;

const StyledBackButton = styled.View`
  margin-top: 24px;
`;

const StudyEventFormScreen: React.FC = () => {
  const { user } = useUserContext();
  const navigation = useNavigation<StackNavigationProp<StudiesParamList>>();
  const route = useRoute<RouteProp<StudiesParamList, 'StudyEventForm'>>();
  const {
    studyParticipantId,
    participantEventId,
    participantEventLogId,
    occurrenceCount
  } = route.params || {};
  const participantEventLogIdRef = useRef(participantEventLogId);
  participantEventLogIdRef.current = participantEventLogId;
  // console.log('participantEventLogId', participantEventLogId);
  // get formTemplate, entity, entityData
  const {
    studyParticipantEventForm,
    loadingStudyParticipantEventForm,
    loadingStudyParticipantEventFormError,
    getStudyParticipantEventForm
  } = useGetStudyParticipantEventForm(studyParticipantId, participantEventId);
  // console.log('studyParticipantEventForm', studyParticipantEventForm);
  // Load the participant event form data on mount as well as refresh the data
  // when the screen comes into focus from the route navigation via
  // useFocusEffect to prevent display of previous state.
  useFocusEffect(
    useCallback(() => {
      const params = {} as any;
      if (participantEventLogId)
        params.participantEventLogId = participantEventLogId;
      getStudyParticipantEventForm(params);
    }, [getStudyParticipantEventForm, participantEventLogId])
  );
  const [formValues, setFormValues] = useState(
    studyParticipantEventForm?.entityData
  );
  const [loadingElements, setLoadingElements] = useState(false);
  const participantEvent = studyParticipantEventForm?.participantEvent;
  const formTemplate = participantEvent?.formTemplate;
  const statePath = route.params?.path;
  const history = useMemo(
    () => parseSectionHistory(route.params?.history),
    [route.params?.history]
  );
  // Set form values once event form metadata is loaded
  useEffect(() => {
    if (studyParticipantEventForm?.entityData) {
      setFormValues(studyParticipantEventForm?.entityData);
    } else {
      // set to empty object so dynamic form will reset if navigating from
      // form to form
      setFormValues({});
    }
  }, [studyParticipantEventForm]);

  const intendedStartDate = useMemo(() => {
    if (participantEvent) {
      return getEventOccurrenceDate(participantEvent, occurrenceCount);
    }
  }, [occurrenceCount, participantEvent]);

  // Local draft (CD-222): survive an app kill mid-section. Restored on a
  // fresh open only (mid-walk section pushes carry `path`); the server copy
  // always wins per field.
  const draft = useFormDraft({
    userId: user?.id,
    formTemplateId: formTemplate?.id,
    instanceId: participantEventLogId,
    formTemplateVersion: formTemplate?.version,
    formConfig: formTemplate?.config
  });
  const [restoredStatePath, setRestoredStatePath] = useState<string>();
  const [restoredHistory, setRestoredHistory] = useState<string[]>();
  const [draftChecked, setDraftChecked] = useState(false);
  useEffect(() => {
    if (draftChecked || !formTemplate) return;
    if (!draft.enabled || statePath) {
      setDraftChecked(true);
      return;
    }
    let stale = false;
    draft.restoreDraft().then(payload => {
      if (stale) return;
      if (payload) {
        setFormValues((current: any) =>
          mergeDraftValues(current || {}, payload.formValues)
        );
        if (payload.history) setRestoredHistory(payload.history);
        if (payload.statePath) {
          setRestoredStatePath(payload.statePath);
          // seed the writer so later value-only writes keep the resume point
          draft.saveDraftStatePath(payload.statePath, payload.history);
        }
      }
      setDraftChecked(true);
    });
    return () => {
      stale = true;
    };
  }, [draft, draftChecked, formTemplate, statePath]);
  // mid-walk sections seed their own path so drafts resume where typed
  useEffect(() => {
    if (draft.enabled && statePath)
      draft.saveDraftStatePath(statePath, history);
  }, [draft, statePath, history]);
  const backApiRef = useRef<DynamicFormBackApi | undefined>(undefined);
  const { confirmLeave } = useFormNavigation();
  const currentHistory = restoredHistory ?? history;
  const currentPath =
    statePath ??
    restoredStatePath ??
    (formTemplate?.config?.states?.[0]
      ? `/${formTemplate.config.name}/${formTemplate.config.states[0].name}`
      : undefined);
  useFocusEffect(
    useCallback(() => {
      if (!backApiRef.current) return;
      backApiRef.current.syncToPath(statePath ?? restoredStatePath);
      if (draft.enabled && currentPath) {
        draft.saveDraftStatePath(currentPath, currentHistory);
      }
    }, [statePath, restoredStatePath, draft, currentPath, currentHistory])
  );
  useRegisterFormBack(async () => {
    const state = navigation.getState();
    const previous = state.routes[state.index - 1];
    const previousParams = (previous?.params || {}) as {
      participantEventId?: number;
    };
    if (
      previous?.name === route.name &&
      String(previousParams.participantEventId) === String(participantEventId)
    ) {
      return false;
    }
    const step = backApiRef.current?.goBack();
    if (step) {
      setRestoredHistory(step.history);
      navigation.setParams({
        path: step.path,
        history: JSON.stringify(step.history)
      });
      return true;
    }
    if (backApiRef.current?.hasUnsavedAnswers()) {
      return !(await confirmLeave());
    }
    return false;
  });
  // console.log('formValues', formValues);
  if (loadingStudyParticipantEventFormError?.statusCode === 403) {
    return (
      <StyledUnavailableView>
        <AlertBox severity='info'>
          This activity is not available to complete from this account.
        </AlertBox>
        <StyledBackButton>
          <Button fullWidth onPress={() => navigation.pop()}>
            Back
          </Button>
        </StyledBackButton>
      </StyledUnavailableView>
    );
  }
  return !loadingStudyParticipantEventForm && formTemplate && draftChecked ? (
    <DynamicFormProvider
      studyParticipantId={studyParticipantId}
      participantUser={participantEvent.studyParticipant?.user}
      entitySchema={formTemplate?.entity}
      formStateNode={formTemplate?.config}
      onLoadProgress={loadingElementQueue => {
        setLoadingElements(loadingElementQueue.length > 0);
      }}
      onChange={values => {
        draft.saveDraftValues(values);
      }}
    >
      <StudyEventFormDataSourceProvider
        studyParticipantId={+studyParticipantId}
        participantEventId={+participantEventId}
        expireDayCount={participantEvent.expireDayCount}
        participantEventLogId={
          participantEventLogId ? +participantEventLogId : undefined
        }
        intendedStartDate={intendedStartDate}
        occurrenceCount={+occurrenceCount}
        formTemplate={formTemplate}
        formData={formValues}
        onCreated={participantEventLog => {
          // let onStatePathChange change navigation and use
          // participantEventLogIdRef to implement synchronously
          participantEventLogIdRef.current = participantEventLog.id;
        }}
        onSubmitted={isFinal => {
          if (isFinal) draft.clearDraft();
        }}
      >
        <StudyEventFormDataSourceContext.Consumer>
          {context => (
            <DynamicForm
              values={formValues}
              statePath={statePath ?? restoredStatePath}
              restoredHistory={currentHistory}
              savedValues={studyParticipantEventForm?.entityData}
              backApiRef={backApiRef}
              dataSourceContext={context}
              onBack={() => {
                navigation.pop();
              }}
              onStatePathChange={(
                path,
                stateNode,
                sectionIndex,
                sectionCount
              ) => {
                const nextHistory = appendSectionHistory(
                  currentHistory,
                  currentPath
                );
                // write-through so a kill during the transition resumes here
                draft.saveDraftStatePath(path, nextHistory);
                if (sectionIndex < sectionCount) {
                  if (participantEventLogId === undefined) {
                    navigation.setParams({
                      studyParticipantId,
                      participantEventId,
                      participantEventLogId: participantEventLogIdRef.current,
                      occurrenceCount
                    });
                  }
                  if (statePath !== path) {
                    navigation.push('StudyEventForm', {
                      path,
                      history: JSON.stringify(nextHistory),
                      sectionIndex: sectionIndex + 1,
                      studyParticipantId,
                      participantEventId,
                      participantEventLogId: participantEventLogIdRef.current,
                      occurrenceCount
                    });
                  }
                } else if (sectionIndex >= sectionCount) {
                  // NOTE: technically this would never occur (you can't configure
                  // a form transition past the last section)
                  navigation.dispatch(
                    StackActions.replace('DrawerNavigator', {
                      screen: 'MyChloe'
                    })
                  );
                }
              }}
            />
          )}
        </StudyEventFormDataSourceContext.Consumer>
      </StudyEventFormDataSourceProvider>
      {loadingElements && (
        <ActivityIndicatorScreen modal backgroundColor='#fff' />
      )}
    </DynamicFormProvider>
  ) : (
    <ActivityIndicatorScreen />
  );
};

export default StudyEventFormScreen;
